import React, { useCallback, useEffect, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { BeatLoader } from 'react-spinners'
import Sidebar from '../../layouts/Sidebar'
import Topbar from '../../layouts/Topbar'
import api from '../../config/api'
import { fetchCompanyAndGeneratePDF } from '../../utils/documentPdf'

/* ─── helpers ─────────────────────────────────────────────────────────────── */

const getErrorMessage = (err, fallback) => {
    const data = err.response?.data
    if (!data) return fallback
    if (typeof data === 'string') return data
    if (data.detail) return data.detail
    if (data.message) return data.message
    return Object.entries(data)
        .map(([f, m]) => `${f}: ${Array.isArray(m) ? m.join(', ') : m}`)
        .join(' | ')
}

const formatDate = (dateStr) => {
    if (!dateStr) return '—'
    const date = new Date(dateStr)
    return Number.isNaN(date.getTime())
        ? dateStr
        : date.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })
}

const formatAmount = (amount, currency) => {
    if (amount == null || amount === '') return '—'
    const num = Number(amount)
    const formatted = Number.isNaN(num)
        ? amount
        : num.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })

    if (currency === 'USD') return `$${formatted}`
    if (currency === 'ZIG') return `ZIG ${formatted}`
    return formatted
}

const getClientName = (clientName, clientDetails) => {
    if (clientDetails?.display_name) return clientDetails.display_name
    if (clientName) return clientName
    if (!clientDetails) return '—'
    const name = [clientDetails.first_name, clientDetails.last_name].filter(Boolean).join(' ')
    return name || clientDetails.email || '—'
}

const getStatusBadgeClass = (status) => {
    switch (status?.toLowerCase?.()) {
        case 'accepted': return 'badge-soft-success'
        case 'draft': return 'badge-soft-warning'
        case 'sent': return 'badge-soft-primary'
        case 'declined':
        case 'expired': return 'badge-soft-danger'
        default: return 'badge-soft-secondary'
    }
}

const formatStatusLabel = (status) => {
    if (!status) return '—'
    return status.charAt(0).toUpperCase() + status.slice(1).toLowerCase()
}

/* ─── component ───────────────────────────────────────────────────────────── */

const QuotationDetails = () => {
    const { id } = useParams()
    const navigate = useNavigate()

    const [quotation, setQuotation] = useState(null)
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState(null)
    const [statusUpdating, setStatusUpdating] = useState(false)
    const [actionMsg, setActionMsg] = useState(null)

    /* ── fetch ── */
    const fetchData = useCallback(async () => {
        setLoading(true)
        setError(null)
        try {
            const res = await api.get(`/quotations/${id}/`)
            setQuotation(res.data)
        } catch (err) {
            console.error('Failed to fetch quotation', err)
            setError(getErrorMessage(err, 'Quotation not found.'))
            setQuotation(null)
        } finally {
            setLoading(false)
        }
    }, [id])

    useEffect(() => {
        fetchData()
    }, [fetchData])

    /* ── update status via PATCH ── */
    const updateStatus = async (newStatus) => {
        if (!quotation) return
        setStatusUpdating(true)
        setActionMsg(null)
        try {
            const res = await api.patch(`/quotations/${id}/`, { status: newStatus })
            setQuotation(res.data)
            setActionMsg(`Status updated to "${formatStatusLabel(newStatus)}".`)
        } catch (err) {
            alert(getErrorMessage(err, 'Failed to update status.'))
        } finally {
            setStatusUpdating(false)
        }
    }

    /* ── print / pdf ── */
    const printQuotation = () => {
        if (!quotation) return
        fetchCompanyAndGeneratePDF(api, quotation, 'quotation')
    }

    /* ── delete ── */
    const handleDelete = async () => {
        if (!quotation) return
        const label = quotation.quotation_number || 'this quotation'
        if (!window.confirm(`Permanently delete ${label}? This cannot be undone.`)) return
        try {
            await api.delete(`/quotations/${id}/`)
            navigate('/quotations')
        } catch (err) {
            alert(getErrorMessage(err, 'Failed to delete quotation.'))
        }
    }

    /* ── loading state ── */
    if (loading) {
        return (
            <div data-sidebar-skin="dark" data-header-skin="light" data-navbar-brand-skin="dark" data-sidebar-state="expand">
                <Topbar />
                <Sidebar />
                <div className="page-wrapper d-flex align-items-center justify-content-center" style={{ minHeight: '100vh' }}>
                    <BeatLoader color="#3550DC" size={15} />
                </div>
            </div>
        )
    }

    /* ── error state ── */
    if (error || !quotation) {
        return (
            <div data-sidebar-skin="dark" data-header-skin="light" data-navbar-brand-skin="dark" data-sidebar-state="expand">
                <Topbar />
                <Sidebar />
                <div className="page-wrapper">
                    <div className="content pb-0">
                        <div className="alert alert-danger m-3" role="alert">
                            <i className="ti ti-alert-circle me-2" />
                            {error || 'Quotation not found.'}
                        </div>
                        <Link to="/quotations" className="btn btn-light ms-3">
                            <i className="ti ti-arrow-left me-1" /> Back to Quotations
                        </Link>
                    </div>
                </div>
            </div>
        )
    }

    /* ── derived values ── */
    const client = quotation.client_details || {}
    const clientName = getClientName(quotation.client_name, quotation.client_details)
    const companyName = quotation.company_name || ''

    return (
        <div data-sidebar-skin="dark" data-header-skin="light" data-navbar-brand-skin="dark" data-sidebar-state="expand">
            <Topbar />
            <Sidebar />

            <div className="page-wrapper">
                <div className="content pb-0">

                    {/* Page header */}
                    <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-4">
                        <div>
                            <h4 className="fw-bold mb-1">Quotation Details</h4>
                            <p className="text-muted mb-0">Quotation #{quotation.quotation_number}</p>
                        </div>
                        <div className="d-flex align-items-center gap-2 flex-wrap">
                            <Link to="/quotations" className="btn btn-light btn-sm">
                                <i className="ti ti-arrow-left me-1" />
                                Back
                            </Link>
                            <Link to={`/edit-quotation/${quotation.id}`} className="btn btn-outline-secondary btn-sm">
                                <i className="ti ti-edit me-1" />
                                Edit Quotation
                            </Link>
                            <button type="button" onClick={printQuotation} className="btn btn-outline-danger btn-sm">
                                <i className="ti ti-file-type-pdf me-1" />
                                Print / PDF
                            </button>
                            <button type="button" onClick={handleDelete} className="btn btn-outline-danger btn-sm">
                                <i className="ti ti-trash me-1" />
                                Delete
                            </button>
                        </div>
                    </div>

                    {/* Action feedback */}
                    {actionMsg && (
                        <div className="alert alert-success alert-dismissible mb-3" role="alert">
                            <i className="ti ti-check me-2" />{actionMsg}
                            <button type="button" className="btn-close" onClick={() => setActionMsg(null)} />
                        </div>
                    )}

                    <div className="row g-4">
                        {/* ── Main card ── */}
                        <div className="col-lg-9">
                            <div className="card shadow-sm mb-4">
                                <div className="card-body p-4">

                                    {/* Header */}
                                    <div className="d-flex justify-content-between align-items-start border-bottom pb-4 mb-4 flex-wrap gap-3">
                                        <div>
                                            {quotation.company_logo && (
                                                <img
                                                    src={quotation.company_logo}
                                                    alt="Company logo"
                                                    style={{
                                                        height: 48,
                                                        maxWidth: 160,
                                                        objectFit: 'contain',
                                                        marginBottom: 8,
                                                    }}
                                                />
                                            )}
                                            <h5 className="fw-bold text-dark m-0">

                                            </h5>
                                            {quotation.title && (
                                                <p className="text-muted fs-13 mb-0">{quotation.title}</p>
                                            )}
                                        </div>
                                        <div className="text-end">
                                            <h3 className="fw-bold text-primary mb-1">QUOTATION</h3>
                                            <span className="fs-14 fw-semibold text-dark">#{quotation.quotation_number}</span>
                                            <div className="mt-2 text-muted fs-13">
                                                <div><strong>Issue Date:</strong> {formatDate(quotation.issue_date)}</div>
                                                <div><strong>Expiry Date:</strong> {formatDate(quotation.expiry_date)}</div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Client info */}
                                    <div className="row mb-4">
                                        <div className="col-md-6">
                                            <h6 className="text-uppercase text-muted fs-12 fw-bold mb-2">Quoted To</h6>
                                            <h5 className="fw-bold text-dark mb-1">{clientName}</h5>
                                            {client.display_name && clientName !== client.display_name && (
                                                <p className="text-muted fs-13 mb-1">
                                                    {[client.first_name, client.last_name].filter(Boolean).join(' ')}
                                                </p>
                                            )}
                                            <p className="text-muted fs-13 mb-1">{client.email || '—'}</p>
                                            <p className="text-muted fs-13 mb-1">{client.phone || '—'}</p>
                                            <p className="text-muted fs-13 mb-0">
                                                {[client.address_line1, client.address_line2, client.city, client.country]
                                                    .filter(Boolean)
                                                    .join(', ')}
                                            </p>
                                        </div>
                                        <div className="col-md-6 text-md-end mt-3 mt-md-0">
                                            <h6 className="text-uppercase text-muted fs-12 fw-bold mb-2">Details</h6>
                                            <div className="fs-13">
                                                <div className="mb-1"><strong>Currency:</strong> {quotation.currency}</div>
                                                <div>
                                                    <strong>Status:</strong>{' '}
                                                    <span className={`badge ${getStatusBadgeClass(quotation.status)} ms-1`}>
                                                        {formatStatusLabel(quotation.status)}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Line items table */}
                                    <div className="table-responsive mb-4">
                                        <table className="table table-hover border">
                                            <thead className="table-light">
                                                <tr>
                                                    <th>Item Name / Description</th>
                                                    <th className="text-end" style={{ width: '100px' }}>Quantity</th>
                                                    <th className="text-end" style={{ width: '150px' }}>Unit Price</th>
                                                    <th className="text-end" style={{ width: '150px' }}>Total</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {(quotation.line_items || []).map((item) => (
                                                    <tr key={item.id || `${item.name}-${item.sort_order}`}>
                                                        <td>
                                                            <div className="fw-semibold text-dark">{item.name}</div>
                                                            {item.description && <small className="text-muted">{item.description}</small>}
                                                        </td>
                                                        <td className="text-end">{item.quantity}</td>
                                                        <td className="text-end">{formatAmount(item.unit_price, quotation.currency)}</td>
                                                        <td className="text-end fw-semibold text-dark">
                                                            {formatAmount(
                                                                item.line_total || (Number(item.quantity) * Number(item.unit_price)),
                                                                quotation.currency
                                                            )}
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>

                                    {/* Totals */}
                                    <div className="row justify-content-end mb-4">
                                        <div className="col-md-5 col-sm-7">
                                            <div className="bg-light p-3 rounded border">
                                                <div className="d-flex justify-content-between mb-2">
                                                    <span className="text-muted">Sub Total</span>
                                                    <span className="fw-semibold text-dark">
                                                        {formatAmount(quotation.subtotal, quotation.currency)}
                                                    </span>
                                                </div>
                                                <div className="d-flex justify-content-between border-top pt-2">
                                                    <span className="fw-bold text-dark">Grand Total</span>
                                                    <span className="fw-bold text-primary fs-16">
                                                        {formatAmount(quotation.grand_total, quotation.currency)}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Terms & Notes */}
                                    <div className="row g-3 border-top pt-4">
                                        {quotation.payment_instructions && (
                                            <div className="col-md-6">
                                                <h6 className="text-uppercase text-muted fs-12 fw-bold mb-2">Terms</h6>
                                                <p className="text-muted fs-13 mb-0" style={{ whiteSpace: 'pre-line' }}>
                                                    {quotation.payment_instructions}
                                                </p>
                                            </div>
                                        )}
                                        {quotation.notes && (
                                            <div className="col-md-6">
                                                <h6 className="text-uppercase text-muted fs-12 fw-bold mb-2">Notes</h6>
                                                <p className="text-muted fs-13 mb-0" style={{ whiteSpace: 'pre-line' }}>
                                                    {quotation.notes}
                                                </p>
                                            </div>
                                        )}
                                    </div>

                                </div>
                            </div>
                        </div>

                        {/* ── Sidebar — Status Actions ── */}
                        <div className="col-lg-3">
                            <div className="card shadow-sm mb-4">
                                <div className="card-header">
                                    <h6 className="fw-bold mb-0">Update Status</h6>
                                </div>
                                <div className="card-body">
                                    <p className="text-muted fs-13 mb-3">Change the status of this quotation:</p>
                                    <div className="d-grid gap-2">
                                        <button
                                            type="button"
                                            onClick={() => updateStatus('sent')}
                                            disabled={statusUpdating || quotation.status === 'sent'}
                                            className="btn btn-primary btn-sm text-white"
                                        >
                                            Mark as Sent
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => updateStatus('accepted')}
                                            disabled={statusUpdating || quotation.status === 'accepted'}
                                            className="btn btn-success btn-sm text-white"
                                        >
                                            Mark as Accepted
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => updateStatus('declined')}
                                            disabled={statusUpdating || quotation.status === 'declined'}
                                            className="btn btn-danger btn-sm text-white"
                                        >
                                            Mark as Declined
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => updateStatus('draft')}
                                            disabled={statusUpdating || quotation.status === 'draft'}
                                            className="btn btn-warning btn-sm text-white"
                                        >
                                            Reset to Draft
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => updateStatus('expired')}
                                            disabled={statusUpdating || quotation.status === 'expired'}
                                            className="btn btn-outline-secondary btn-sm"
                                        >
                                            Mark as Expired
                                        </button>
                                    </div>
                                    {statusUpdating && (
                                        <div className="text-center mt-3">
                                            <BeatLoader color="#3550DC" size={8} />
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="footer d-flex align-items-center justify-content-between flex-column flex-sm-row row-gap-2 border-top py-2 px-3">
                    <p className="text-dark mb-0">
                        {new Date().getFullYear()} © <span className="link-primary">Burray Invoice</span>, All Rights Reserved
                    </p>
                </div>
            </div>
        </div>
    )
}

export default QuotationDetails
